/* External dependencies */
import PropTypes from "prop-types";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { defineMessages, FormattedDate, FormattedMessage, injectIntl, intlShape } from "react-intl";

/* Internal dependencies */
import ButtonsContainer from "../../general/ButtonsContainer";
import ErrorDisplay, { ErrorPropTypeShape } from "../../../errors/ErrorDisplay";
import { toSafeInteger } from "lodash/lang";
import { Button, Modal, TextInput } from "@yoast/ui-library";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/solid";


import * as styles from "./styles.module.scss";
import { SubscriptionDetailContext } from "../../../context/SubscriptionDetailContext";
import { IconButton } from "../../IconButton";

const messages = defineMessages( {
	ariaLabel: {
		id: "subscriptionCancel.modal.arialabel",
		defaultMessage: "Cancel subscription",
	},
	header: {
		id: "subscriptionCancel.modal.header",
		defaultMessage: "Cancel subscription",
	},
	areYouSure: {
		id: "subscriptionCancel.modal.body.areYouSure",
		defaultMessage: "Are you sure you want to cancel this subscription?",
	},
	consequences: {
		id: "subscriptionCancel.modal.body.consequences",
		defaultMessage: "If you proceed, you will lose support, security updates, and functionality updates after {term}.",
	},
	yourUsage: {
		id: "subscriptionCancel.modal.body.yourUsage",
		defaultMessage: "Your subscription usage ({amount} seats)",
	},
	connectedSites: {
		id: "subscriptionCancel.modal.body.connectedSites",
		defaultMessage: "Connected sites: {amount} / {limit}",
	},
	linkedGoogleAccounts: {
		id: "subscriptionCancel.modal.body.linkedGoogleAccounts",
		defaultMessage: "Linked Google accounts: {amount} / {limit}",
	},
	important: {
		id: "subscriptionCancel.modal.body.important",
		defaultMessage: "Important: ",
	},
	disclaimer: {
		id: "subscriptionCancel.modal.body.disclaimer",
		defaultMessage: "Once canceled, these seats cannot be restored.",
	},
	cancel: {
		id: "subscriptionCancel.modal.cancel",
		defaultMessage: "Keep subscription",
	},
	confirm: {
		id: "subscriptionCancel.modal.confirm",
		defaultMessage: "Confirm cancellation",
	},
	loading: {
		id: "subscriptionCancel.modal.loading",
		defaultMessage: "Cancelling subscription...",
	},
	amount: {
		id: "subscriptionCancel.modal.amount",
		defaultMessage: "Amount",
	},
	selectAmount: {
		id: "subscriptionCancel.modal.selectAmount",
		defaultMessage: "Seats to cancel",
	},
	decreaseAmount: {
		id: "subscriptionCancel.modal.decreaseAmount",
		defaultMessage: "Decrease amount",
	},
	increaseAmount: {
		id: "subscriptionCancel.modal.increaseAmount",
		defaultMessage: "Increase amount",
	},
} );

const SubscriptionEditModal = ( props ) => {
	const { subscriptionId, subscription, request } = useContext( SubscriptionDetailContext );
	const [ amountToCancel, setAmountToCancel ]     = useState( 0 );
	const needsReloadOnSuccess                      = useRef( false );

	useEffect( props.onClose, [ props.success ] );

	useEffect( () => {
		if ( props.success && needsReloadOnSuccess.current ) {
			request.reload();
		}
	}, [ props.success ] );

	const cancelSubscription = useCallback( () => {
		if ( ! amountToCancel > 0 ) {
			return;
		}

		needsReloadOnSuccess.current = true;
		// The name comes from the subscription detail response this modal already renders, so the
		// analytics event no longer depends on the subscription happening to be in the entity map.
		props.cancelSubscription( subscriptionId, amountToCancel, subscription.name );
	}, [ props.cancelSubscription, amountToCancel, subscriptionId, subscription.name ] );

	/**
	 * Validates the input against the maximum number of subscriptions that is possible to cancel
	 * and sets it in the state.
	 *
	 * @param {Object} event The change event
	 *
	 * @returns {void}
	 */
	const changeItemsToCancel = useCallback( ( event ) => {
		event.preventDefault();
		const value = toSafeInteger( event.target.value );
		// Makes sure the value is between 0 and the maximum number of subscriptions
		setAmountToCancel( Math.min( Math.max( value, 0 ), subscription.limit ) );
	}, [ setAmountToCancel, subscription.limit ] );

	/**
	 * Increase the number of items to cancel unless it is already at the maximum possible.
	 *
	 * @returns {void}
	 */
	const incrementItemsToCancel = useCallback( () => {
		if ( amountToCancel < subscription.limit ) {
			setAmountToCancel( amountToCancel + 1 );
		}
	}, [ amountToCancel, setAmountToCancel, subscription.limit ] );

	/**
	 * Decrease the number of items to cancel unless it is already at one.
	 *
	 * @returns {void}
	 */
	const decrementItemsToCancel = useCallback( () => {
		if ( amountToCancel > 0 ) {
			setAmountToCancel( amountToCancel - 1 );
		}
	}, [ amountToCancel, setAmountToCancel ] );


	const displayNumberButtons = () => {
		if ( subscription.limit ) {
			return <div className={ styles.amountSelector }>
				<label htmlFor="input-number">
					<FormattedMessage
						id={ messages.selectAmount.id }
						defaultMessage={ messages.selectAmount.defaultMessage }
					/>
				</label>

				<IconButton
					className={ styles.amountButton }
					onClick={ decrementItemsToCancel }
					variant="secondary"
					size="small"
					aria-label={ messages.decreaseAmount.defaultMessage }
				>
					<MinusIcon />
				</IconButton>
				<TextInput
					id="input-number"
					className={ styles.amountInput }
					value={ amountToCancel }
					onChange={ changeItemsToCancel }
					type="number"
					aria-label={ messages.amount.defaultMessage }
				/>
				<IconButton
					className={ styles.amountButton }
					onClick={ incrementItemsToCancel }
					variant="secondary"
					size="small"
					aria-label={ messages.increaseAmount.defaultMessage }
				>
					<PlusIcon />
				</IconButton>
			</div>;
		}
	};

	const confirmButtonText = props.loading ? messages.loading : messages.confirm;
	const isEnabled         = props.loading === false && amountToCancel > 0;
	const paymentTermDate   = <FormattedDate
		value={ subscription.validUntil }
		year="numeric"
		month="long"
		day="2-digit"
	/>;

	return <Modal isOpen={ props.isOpen } onClose={ props.onClose } position="center">
		<Modal.Panel>
			<Modal.Title size={ "2" } className={ styles.modalTitle }>
				<FormattedMessage { ...messages.header } />
			</Modal.Title>
			<div className={ styles.cancelForm }>
				<FormattedMessage tagName={ "p" } { ...messages.areYouSure } />
				<FormattedMessage
					tagName={ "p" }
					{ ...messages.consequences }
					values={ { term: <strong>{ paymentTermDate }</strong> } }
				/>

				<hr />

				<p className={ styles.listLabel }>
					<FormattedMessage
						tagName={ "strong" }
						{ ...messages.yourUsage }
						values={ { amount: subscription.limit } }
					/>
				</p>
				<ul>
					{ subscription.included.inSubscriptionGroup.plugins.length > 0 && <FormattedMessage
						tagName={ "li" }
						{ ...messages.connectedSites }
						values={ {
							amount: <strong>{ subscription.connectedSites.length }</strong>,
							limit: subscription.limit,
						} }
					/>
					}
					{ subscription.included.inSubscriptionGroup.invitableServices.length > 0 && <FormattedMessage
						tagName={ "li" }
						{ ...messages.linkedGoogleAccounts }
						values={ {
							amount: <strong>{ subscription.emailInvites.used }</strong>,
							limit: subscription.limit,
						} }
					/>
					}
				</ul>

				<hr />

				{ displayNumberButtons() }
				<p>
					<em>
						<FormattedMessage tagName={ "strong" } { ...messages.important } />
						<FormattedMessage { ...messages.disclaimer } />
					</em>
				</p>
				<ErrorDisplay error={ props.error } />
				<ButtonsContainer>
					<Button
						variant="secondary"
						onClick={ props.onClose }
					>
						<FormattedMessage { ...messages.cancel } />
					</Button>
					<Button
						type="submit"
						onClick={ cancelSubscription }
						disabled={ ! isEnabled }
						isLoading={ props.loading }
						variant="error"
					>
						<FormattedMessage { ...confirmButtonText } />
					</Button>
				</ButtonsContainer>
			</div>
		</Modal.Panel>
	</Modal>;
};

SubscriptionEditModal.propTypes = {
	intl: intlShape.isRequired,
	isOpen: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	cancelSubscription: PropTypes.func.isRequired,
	loading: PropTypes.bool.isRequired,
	success: PropTypes.bool.isRequired,
	error: ErrorPropTypeShape,
};

SubscriptionEditModal.defaultProps = {
	isOpen: false,
	error: null,
};

export default injectIntl( SubscriptionEditModal );
